/**
 * useMenuNavigation Hook
 * Provides enhanced navigation utilities with menu context
 */

import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMenu } from '@/providers/MenuProvider';
import { FlatMenuItem, MenuModule } from '@/services/menu.service';

interface UseMenuNavigationReturn {
  // Current route info
  currentRoute: string;
  currentMenuItem: FlatMenuItem | undefined;
  currentModule: MenuModule | undefined;
  breadcrumb: string[];
  
  // Navigation utilities
  navigateTo: (route: string) => void;
  goBack: () => void;
  
  // Related navigation
  relatedScreens: FlatMenuItem[];
  quickActions: FlatMenuItem[];
  
  // Menu visibility
  isMenuItemActive: (route: string) => boolean;
  isModuleActive: (moduleId: string) => boolean;
  
  // Module info
  getCurrentModuleId: () => string | undefined;
  getModuleColor: () => string;
}

/**
 * Module color mapping for visual consistency - Oracle-style 10 domains
 */
const MODULE_COLORS: Record<string, string> = {
  home: '#145A54',
  sales: '#52c41a',
  scm: '#faad14',
  mfg: '#13c2c2',
  fin: '#389e0d',
  qms: '#f5222d',
  prj: '#145A54',
  hr: '#fa541c',
  analytics: '#722ed1',
  platform: '#595959',
  
  // Legacy mappings for backwards compatibility
  dashboard: '#145A54',
  'sales-crm': '#52c41a',
  engineering: '#722ed1',
  planning: '#fa8c16',
  production: '#13c2c2',
  inventory: '#eb2f96',
  procurement: '#faad14',
  quality: '#f5222d',
  maintenance: '#2f54eb',
  finance: '#389e0d',
  projects: '#145A54',
  estimation: '#722ed1',
  compliance: '#8c8c8c',
  administration: '#595959',
};

/**
 * useMenuNavigation Hook
 */
export const useMenuNavigation = (): UseMenuNavigationReturn => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    getMenuItemByRoute,
    getModuleByRoute,
    getBreadcrumb,
    getRelatedScreens,
    getQuickActions,
  } = useMenu();

  // Current route
  const currentRoute = location.pathname;

  // Get current menu item
  const currentMenuItem = useMemo(() => {
    return getMenuItemByRoute(currentRoute);
  }, [currentRoute, getMenuItemByRoute]);

  // Get current module
  const currentModule = useMemo(() => {
    return getModuleByRoute(currentRoute);
  }, [currentRoute, getModuleByRoute]);

  // Get breadcrumb
  const breadcrumb = useMemo(() => {
    return getBreadcrumb(currentRoute);
  }, [currentRoute, getBreadcrumb]);

  // Get related screens (same module)
  const relatedScreens = useMemo(() => {
    return getRelatedScreens(currentRoute, 5);
  }, [currentRoute, getRelatedScreens]);

  // Get quick actions for current module
  const quickActions = useMemo(() => {
    if (!currentModule) return [];
    return getQuickActions(currentModule.id);
  }, [currentModule, getQuickActions]);

  // Navigation utilities
  const navigateTo = (route: string) => {
    navigate(route);
  };

  const goBack = () => {
    navigate(-1);
  };

  // Check if a menu item is active
  const isMenuItemActive = (route: string): boolean => {
    if (route === currentRoute) return true;
    
    // Check if current route starts with the menu route (for nested routes)
    if (currentRoute.startsWith(route) && route !== '/') {
      return true;
    }
    
    return false;
  };

  // Check if a module is active
  const isModuleActive = (moduleId: string): boolean => {
    return currentModule?.id === moduleId;
  };

  // Get current module ID
  const getCurrentModuleId = (): string | undefined => {
    return currentModule?.id;
  };

  // Get module color
  const getModuleColor = (): string => {
    if (!currentModule) return '#145A54';
    return MODULE_COLORS[currentModule.id] || '#145A54';
  };

  return {
    currentRoute,
    currentMenuItem,
    currentModule,
    breadcrumb,
    navigateTo,
    goBack,
    relatedScreens,
    quickActions,
    isMenuItemActive,
    isModuleActive,
    getCurrentModuleId,
    getModuleColor,
  };
};

export default useMenuNavigation;
